import { UK_PHONE_NUMBER, UK_PHONE_NUMBER_INTERNATIONAL, IE_PHONE_NUMBER } from '../constants/basicData';

import { useIsIrishUser, isIrishUserCookieValue, getBrowserCookies } from './cookies';

type Format = 'spaced' | 'compact';

function format(phone: string, fmt: Format): string {
    return fmt === 'spaced' ? phone : phone.replace(/\s/g, '');
}

function pick(isIrishUser: boolean, isInternational: boolean, hasCookie: boolean): string {
    // Legacy semantics: when the geo cookie is missing the helper returned
    // an empty string. Preserve that so the call sites that compare the
    // result against `''` keep working.
    if (!hasCookie) return '';
    if (isIrishUser) return IE_PHONE_NUMBER;
    return isInternational ? UK_PHONE_NUMBER_INTERNATIONAL : UK_PHONE_NUMBER;
}

/**
 * Hook variant that participates in SSR — reads the cookie via React context
 * so the server-rendered HTML matches what the browser will produce on
 * hydrate.
 */
export const usePhoneNumber = (fmt: Format = 'spaced', isInternational = false): string => {
    const isIrishUser = useIsIrishUser();
    const phone = pick(isIrishUser, isInternational, /* hasCookie */ true);
    return format(phone, fmt);
};

/**
 * Pure helper for non-React code (analytics, RxJS epics, content data files
 * imported at module-init time) that doesn't have access to React context.
 * Accepts the raw cookie value so the caller stays in charge of where the
 * cookie comes from.
 */
export const phoneNumberFromCookie = (
    cookieValue: unknown,
    fmt: Format = 'spaced',
    isInternational = false,
): string => {
    if (!cookieValue) return '';
    const isIrishUser = isIrishUserCookieValue(cookieValue);
    const phone = pick(isIrishUser, isInternational, true);
    return format(phone, fmt);
};

/**
 * Browser-only legacy helper kept for tests and a few imperative call sites
 * that aren't inside a React render. New code should prefer the hook above
 * so SSR participates in cookie reads.
 */
export const getPhoneNumber = (fmt: Format = 'spaced', isInternational = false): string => {
    const value = getBrowserCookies().get('isIrishUser', { doNotParse: true });
    return phoneNumberFromCookie(value, fmt, isInternational);
};
